import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Image from "../../../components/images/Image";
import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../../services/useApi";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import config from "../../../config/config";
import { useNavigate, useParams } from "react-router-dom";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import moment from "moment";
import ToolTip from "../../../components/ToolTip/ToolTip";
import CustomModal from "../../../components/CustomModal";
import TextArea from "../../../components/inputfields/TextArea";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/toastMessage/toastSlice";

type TransactionDetails = {
    id: string;
    transaction_id: string;
    transaction_date: string;
    requested_date: string;
    transaction_category: string;
    transaction_category_status: string;
    wallet_type: string;
    requested_by: string;
    requested_from: string;
    payment_type: string;
    amount: string;
    e_topup_stock_transferred: string | null;
    receiver_payment_source: string;
    sender_payment_source: string;
    sender_source_name: string;
    transaction_receipt: string;
    transaction_status: string;
    note: string;
    sender_location: string;
    receiver_location: string;
    phone_number: string | null;
    telecom_name: string | null;
    flagged_reason: string | null;
    rejection_reason: string | null;
    is_flagged: boolean;
};

export default function ViewTransaction() {
    const { getAPI, PatchAPI } = useApi();
    const { translate, notEnglish } = useTranslation(adminContents);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();
    const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reasonInput, setReasonInput] = useState<string>('');
    const [reasonInputError, setReasonInputError] = useState<string>('');
    const [isFlagged, setIsFlagged] = useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [flagStatus, setFlagStatus] = useState<string>('');
    const dispatch = useDispatch();
    const [isFlagDisabledByTime, setIsFlagDisabledByTime] = useState(false);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await getAPI(`transactions/user/self/${id}`);
            if (!response?.error) {
                setTransactionDetails(response?.data?.data);
                setIsFlagged(response?.data?.data?.flagged);
                if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Approved") {
                    setFlagStatus("approved");
                } else if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Pending") {
                    setFlagStatus('pending');
                } else if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Rejected") {
                    setFlagStatus('rejected');
                } else {
                    setFlagStatus('');
                }
            } else {
                navigate("/not-found", {
                    state: { route: "/admin-management/list-of-admins" },
                });
            }
        } catch (error) {
            console.error("Error fetching transaction details:", error);
        }
        setLoading(false);
    };

    useLayoutEffect(() => {
        fetchDetails();
    }, []);

    useEffect(() => {
        if (!transactionDetails) return;
        const transactionDate = moment(transactionDetails.transaction_date);
        const checkDisabled = () => {
            const now = moment();
            const diffMinutes = now.diff(transactionDate, 'minutes');
            if (diffMinutes >= 2880 && flagStatus !== "approved" && flagStatus !== "rejected") {
                setIsFlagDisabledByTime(true);
                return true;
            } else {
                setIsFlagDisabledByTime(false);
                return false;
            }
        };
        // Initial check
        if (checkDisabled()) return;
        // Poll every 10 seconds
        const interval = setInterval(() => {
            if (checkDisabled()) {
                clearInterval(interval);
            }
        }, 10000);
        return () => clearInterval(interval);
    }, [transactionDetails, flagStatus]);

    const handleFlag = async () => {
        setReasonInputError('');
        if (!isFlagged && !reasonInput.trim()) {
            setReasonInputError('This field is required');
            return;
        }
        setButtonLoading(true);
        const payload = isFlagged
            ? { is_flagged: false }
            : { is_flagged: true, reason: reasonInput.trim() };
        try {
            const response = await PatchAPI(`transactions/user/self/flag/${id}`, payload);
            if (!response?.error) {
                setIsFlagged(!isFlagged);
                setIsModalOpen(false);
                setReasonInput('');
                setReasonInputError('');
                dispatch(showToast({
                    type: 'success',
                    message: isFlagged ? 'Transaction unflagged successfully' : 'Transaction flagged successfully and sent for review',
                }));
                fetchDetails();
            } else {
                dispatch(showToast({
                    type: 'error',
                    message: response?.data?.message || 'Something went wrong',
                }));
            }
        } catch (error) {
            dispatch(showToast({
                type: 'error',
                message: 'Something went wrong',
            }));
        } finally {
            setButtonLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setReasonInput('');
        setReasonInputError('');
    };

    const getFlaggingAllowed = () => {
        if (!transactionDetails) return false;
        if (flagStatus === "approved" || flagStatus === "rejected") return true;
        if (transactionDetails.transaction_category === 'Recharge') return true;
        if (transactionDetails.transaction_category_status !== "Approved") return false;
        if (isFlagged) return true;
        if (isFlagDisabledByTime) return false;
        const transactionDate = moment(transactionDetails.transaction_date);
        const now = moment();
        return now.diff(transactionDate, 'minutes') < 2880;
    };

    return (
        <div dir={notEnglish ? "rtl" : "ltr"}>
            <Breadcrumbs
                navPath={[
                    "View Profile",
                    "List of Transactions",
                    "View Transaction Details",
                ]}
                links={["/view-profile", "/view-profile/transaction-details"]}
                heading={`View Transaction`}
            />
            {!loading && transactionDetails ? (
                <div className="m-3 bg-white h-maintableheight rounded-lg flex flex-col">
                    <div className="px-6 py-5 border-b border-[#E5E9EB] flex justify-between items-center">
                        <div>
                            <span className=" text-title3">Transaction ID - </span>
                            <span
                                title={"Transaction"}
                                data-testid="transaction-id"
                                className="text-heading3"
                            >
                                {transactionDetails.transaction_id || "-"}
                            </span>
                        </div>
                        {transactionDetails?.transaction_category !== "Reversal" && (
                            <div
                                className={`w-12 h-12 rounded-full
                                ${flagStatus === "approved"
                                        ? "bg-positive_light"
                                        : flagStatus === "rejected"
                                            ? "bg-negative_light"
                                            : isFlagged
                                                ? "bg-p_light"
                                                : ""
                                    }
                                flex justify-center items-center
                                ${(getFlaggingAllowed())
                                        ? (flagStatus === "approved" || flagStatus === "rejected") ? "cursor-not-allowed"
                                            : flagStatus === '' ? "cursor-pointer" : "cursor-pointer"
                                        : "cursor-not-allowed"
                                    }
                            `}
                            >
                                <Image
                                    testId={
                                        getFlaggingAllowed()
                                            ? flagStatus === "approved"
                                                ? "image-flag-approved"
                                                : flagStatus === "rejected"
                                                    ? "image-flag-rejected"
                                                    : "image-flag"
                                            : "image-flag-disabled"
                                    }
                                    src={
                                        getFlaggingAllowed()
                                            ? flagStatus === "approved"
                                                ? "flag-approved"
                                                : flagStatus === "rejected"
                                                    ? "flag-rejected"
                                                    : "flag"
                                            : "flag-disabled"
                                    }
                                    className="w-6 h-6"
                                    onClick={() => {
                                        if (!getFlaggingAllowed()) {
                                            dispatch(
                                                showToast({
                                                    type: "warning",
                                                    message:
                                                        transactionDetails.transaction_category_status ===
                                                            "Pending" &&
                                                            transactionDetails.transaction_category ===
                                                            "Recharge"
                                                            ? "This transaction is not approved yet"
                                                            : transactionDetails.transaction_category_status !==
                                                                "Approved" &&
                                                                transactionDetails.transaction_category ===
                                                                "eTop Up"
                                                                ? "This transaction is not approved yet"
                                                                : isFlagged
                                                                    ? ""
                                                                    : "Flagging is only allowed within 48 hours of the transaction date",
                                                })
                                            );
                                            return;
                                        }
                                        setIsModalOpen(
                                            flagStatus === "approved" || flagStatus === "rejected"
                                                ? false
                                                : true
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={`flex-grow px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix(
                            "scroll"
                        )} overflow-y-scroll`}
                    >
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Requested Date & Time
                                </h1>
                                <p className="text-body1">
                                    {moment(transactionDetails.requested_date).format(
                                        "DD-MM-YYYY, hh:mm A"
                                    ) || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Transaction Date & Time
                                </h1>
                                <p className="text-body1">
                                    {moment(transactionDetails.transaction_date).format(
                                        "DD-MM-YYYY, hh:mm A"
                                    ) || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-prmary_text">
                                    Transaction Type (Status)
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.transaction_category
                                        ? `${transactionDetails.transaction_category} (${transactionDetails.transaction_category_status})`
                                        : "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">Wallet</h1>
                                <p className="text-body1">
                                    {transactionDetails.wallet_type || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">Requested By</h1>
                                <p className="text-body1">
                                    {transactionDetails.requested_by || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Requested From
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.requested_from || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">Payment Type</h1>
                                <p className="text-body1">
                                    {transactionDetails.payment_type
                                        ? transactionDetails.payment_type
                                            .charAt(0)
                                            .toUpperCase() +
                                        transactionDetails.payment_type.slice(1)
                                        : "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Requested Amount
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.amount
                                        ? `AFA ${transactionDetails.amount}`
                                        : "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">Sent Amount</h1>
                                <p className="text-body1">
                                    {transactionDetails.e_topup_stock_transferred
                                        ? `AFA ${transactionDetails.e_topup_stock_transferred}`
                                        : "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Transaction Status
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.transaction_status || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Sender Payment Source
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.sender_payment_source || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Reciever Payment Source
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.receiver_payment_source || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Source of Payment
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.sender_source_name || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Sender Location
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.sender_location || "-"}
                                </p>
                            </div>
                            <div>
                                <h1 className="text-body2 text-primary_text">
                                    Reciever Location
                                </h1>
                                <p className="text-body1">
                                    {transactionDetails.receiver_location || "-"}
                                </p>
                            </div>
                            <div className="group relative">
                                <h1 className="text-body2 text-primary_text">
                                    Transaction Receipt
                                </h1>
                                {transactionDetails?.transaction_receipt ? (
                                    <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                                        <p className="more-lines">
                                            {transactionDetails?.transaction_receipt
                                                ?.split("/")
                                                .pop()}
                                        </p>
                                        <ToolTip
                                            ToolTipItem={
                                                transactionDetails?.transaction_receipt
                                                    ?.split("/")
                                                    .pop() || ""
                                            }
                                            top="top-5 left-5"
                                        />
                                        <Image
                                            src="file-eye"
                                            className="cursor-pointer "
                                            objectFit={"contain"}
                                            onClick={() => {
                                                const fileUrl =
                                                    config?.cdnURL +
                                                    "public/" +
                                                    transactionDetails.transaction_receipt;
                                                const link = document.createElement("a");
                                                link.href = fileUrl;
                                                link.target = "_blank";
                                                link.rel = "noopener noreferrer";
                                                link.click();
                                            }}
                                        />
                                    </div>
                                ) : (
                                    "-"
                                )}
                            </div>
                        </div>
                        <div className="mt-6">
                            <h1 className="text-body2 w-full text-primary_text">Note</h1>
                            <p className="text-body1 break-all">
                                {transactionDetails.note || "-"}
                            </p>
                        </div>
                        {transactionDetails.flagged_reason && (
                            <div className="mt-6">
                                <h1 className="text-body2 w-full text-primary_text">
                                    Reason for Reversal
                                </h1>
                                <p className="text-body1 break-all">
                                    {transactionDetails.flagged_reason || "-"}
                                </p>
                            </div>
                        )}
                        {transactionDetails.rejection_reason && (
                            <div className="mt-6">
                                <h1 className="text-body2 w-full text-primary_text">
                                    Reason for Rejection
                                </h1>
                                <p className="text-body1 break-all">
                                    {transactionDetails.rejection_reason || "-"}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <Shimmer />
            )}
            {isModalOpen && (
                <CustomModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    title={isFlagged ? "Unflag Transaction" : "Flag Transaction"}
                    subText={
                        isFlagged
                            ? "Are you sure you want to unflag this transaction?"
                            : "Are you sure you want to flag this transaction?"
                    }
                    primaryButtonText={isFlagged ? "Unflag" : "Flag"}
                    secondaryButtonText={translate("Cancel")}
                    secondaryButton={true}
                    handelSubmit={handleFlag}
                    buttonColor={isFlagged ? "bg-p_primary" : "bg-negative"}
                    buttonLoading={buttonLoading}
                    buttonWidth={"w-[108px]"}
                    children={
                        !isFlagged ? (
                            <>
                                <TextArea
                                    label="Reason"
                                    required={true}
                                    placeholderRequied={false}
                                    text={reasonInput}
                                    setText={setReasonInput}
                                    textError={reasonInputError}
                                    setTextError={setReasonInputError}
                                    bgColor={"bg-white"}
                                    maxLength={250}
                                    id={`reason-note`}
                                    height={"h-[140px]"}
                                />
                            </>
                        ) : null
                    }
                />
            )}
        </div>
    );
}
